// src/Components/Marketplace/services/marketplaceCheckout.js
// Reuses the exact Razorpay checkout pattern already used by Flight/Bus/Hotel payment
// flows in this app (see BusPaymentInfo.jsx / Paymentinfo.jsx / HotelPaymentInfo.jsx):
// checkout.js is already loaded globally in public/index.html, open a Razorpay instance
// with the order returned by the backend, then verify on success. The marketplace verify
// endpoint (`/marketplace-payment/verify`) is shared by both service bookings and package
// purchases, so this single helper drives both.
import { verifyMarketplacePayment } from "./marketplaceApi";

export const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

/**
 * Opens Razorpay checkout for a marketplace order, then verifies the payment.
 * @param {Object} opts
 * @param {Object} opts.order - Razorpay order object from the backend ({id, amount, currency}).
 * @param {string} opts.description - Shown in the Razorpay modal.
 * @param {Object} [opts.prefill] - {name, email, contact}
 * @param {Function} opts.onSuccess - (verifyResult, razorpayResponse) => void
 * @param {Function} opts.onError - (error) => void
 * @param {Function} [opts.onDismiss] - called if the user closes the modal without paying.
 */
export const openMarketplaceCheckout = async ({
  order,
  description,
  prefill = {},
  onSuccess,
  onError,
  onDismiss,
}) => {
  if (!order?.id) {
    onError?.(new Error("Payment order could not be created. Please try again."));
    return;
  }

  const loaded = await loadRazorpayScript();
  if (!loaded) {
    onError?.(new Error("Payment system failed to load. Please refresh and try again."));
    return;
  }

  const options = {
    key: process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_live_RMb7SMI7mR2cCA",
    amount: order.amount,
    currency: order.currency || "INR",
    order_id: order.id,
    name: "Bachatwali Ticket",
    description,
    prefill,
    theme: { color: "#027ef6" },
    modal: {
      ondismiss: () => {
        onDismiss?.();
      },
    },
    handler: async (response) => {
      try {
        const verifyResult = await verifyMarketplacePayment({
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        });
        onSuccess?.(verifyResult, response);
      } catch (err) {
        onError?.(err);
      }
    },
  };

  const rzp = new window.Razorpay(options);
  rzp.on("payment.failed", (resp) => {
    onError?.(
      new Error(
        resp?.error?.description || resp?.error?.reason || "Payment failed. Please try again."
      )
    );
  });
  rzp.open();
};
